// src/features/traceability/data/isoMaterialTree.ts


type TranslationFunction = (key: string) => string;

/**
 * Get translated material tree for UI components
 * Use this in React components with useTranslation hook
 */
type TreeOption = {
  label: string;
  value: string;
  children?: TreeOption[];
};

const getIsoMaterialTree = (t: TranslationFunction): TreeOption[] => [
  {
    label: t('traceability.materials.acier'),
    value: 'acier',
    children: [
      { label: t('traceability.materials.lt700'), value: 'lt700' },
      { label: t('traceability.materials.gt700'), value: 'gt700' },
      { label: t('traceability.materials.finemac'), value: 'finemac' },
      { label: t('traceability.materials.inox'), value: 'inox' },
      { label: t('traceability.materials.autre'), value: 'autre' },
    ],
  },
  {
    label: t('traceability.materials.acier_trempe'),
    value: 'acier_trempe',
    children: [
      { label: t('traceability.materials.lt58'), value: 'lt58' },
      { label: t('traceability.materials.lt65'), value: 'lt65' },
      { label: t('traceability.materials.autre'), value: 'autre' },
    ],
  },
  {
    label: t('traceability.materials.aluminium'),
    value: 'aluminium',
    children: [
      { label: t('traceability.materials.lt8'), value: 'lt8' },
      { label: t('traceability.materials.gt8'), value: 'gt8' },
      { label: t('traceability.materials.autre'), value: 'autre' },
    ],
  },
  {
    label: t('traceability.materials.chrome'),
    value: 'chrome',
  },
  {
    label: t('traceability.materials.titane'),
    value: 'titane',
  },
  {
    label: t('traceability.materials.fonte'),
    value: 'fonte',
  },
  {
    label: t('traceability.materials.nickel'),
    value: 'nickel',
  },
  {
    label: t('traceability.materials.bronze'),
    value: 'bronze',
  },
  {
    label: t('traceability.materials.laiton'),
    value: 'laiton',
  },
  {
    label: t('traceability.materials.or'),
    value: 'or',
  },
  {
    label: t('traceability.materials.super'),
    value: 'super',
  },
  {
    label: t('traceability.materials.argent'),
    value: 'argent',
  },
  {
    label: t('traceability.materials.cube'),
    value: 'cube',
  },
  {
    label: t('traceability.materials.cuivre'),
    value: 'cuivre',
  },
  {
    label: t('traceability.materials.durnico'),
    value: 'durnico',
  },
  {
    label: t('traceability.materials.laiton_sans_plomb'),
    value: 'laiton_sans_plomb',
  },
  {
    label: t('traceability.materials.metal_lourd'),
    value: 'metal_lourd',
  },
  {
    label: t('traceability.materials.céramique'),
    value: 'céramique',
  },
  {
    label: t('traceability.materials.composite_carbone'),
    value: 'composite_carbone',
  },
  {
    label: t('traceability.materials.composite_verre'),
    value: 'composite_verre',
  },
  {
    label: t('traceability.materials.graphite'),
    value: 'graphite',
  },
  {
    label: t('traceability.materials.palladium'),
    value: 'palladium',
  },
  {
    label: t('traceability.materials.platine'),
    value: 'platine',
  },
  {
    label: t('traceability.materials.polymere'),
    value: 'polymere',
  },
  {
    label: t('traceability.materials.tantale'),
    value: 'tantale',
  },
  {
    label: t('traceability.materials.autre'),
    value: 'autre',
  },
];

/**
 * Flatten the ISO material tree into a list of { value, label } items.
 * Parent nodes with children produce entries like { value: "acier/inox", label: "Steel — Stainless" }.
 * Top-level leaves (no children) use the leaf value directly: { value: "titane", label: "Titanium" }.
 */
export function flattenIsoTree(
  t: TranslationFunction
): Array<{ value: string; label: string }> {
  const result: Array<{ value: string; label: string }> = [];
  for (const parent of getIsoMaterialTree(t)) {
    if (parent.children?.length) {
      for (const child of parent.children) {
        result.push({
          value: `${String(parent.value)}/${String(child.value)}`,
          label: `${parent.label} — ${child.label}`,
        });
      }
    } else {
      result.push({ value: String(parent.value), label: parent.label });
    }
  }
  return result;
}
